import React, { useState } from 'react';

interface InterestFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  phone: string;
  email: string;
  company: string;
}

const InterestFormModal: React.FC<InterestFormModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    company: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const validatePhone = (phone: string): boolean => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  };

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!formData.name.trim()) { setErrorMessage('Please enter your name.'); return; }
    if (!formData.phone.trim()) { setErrorMessage('Please enter your phone number.'); return; }
    if (!validatePhone(formData.phone)) { setErrorMessage('Please enter a valid 10-digit US phone number.'); return; }
    if (!formData.email.trim()) { setErrorMessage('Please enter your email address.'); return; }
    if (!validateEmail(formData.email)) { setErrorMessage('Please enter a valid email address.'); return; }
    if (!formData.company.trim()) { setErrorMessage('Please enter your company name.'); return; }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_URL || 'https://primary-production-324c.up.railway.app/webhook/da511a5a-1b6c-48f4-8c58-b22c96a5c4ca';
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Webhook-Auth': process.env.REACT_APP_WEBHOOK_TOKEN || ''
        },
        body: JSON.stringify({
          "Name": formData.name,
          "Phone Number": formData.phone,
          "Email": formData.email,
          "Company Name": formData.company,
          "Role": "",
          "Request": "",
          "Company Size": ""
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setFormData({ name: '', phone: '', email: '', company: '' });
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-lg mx-4 bg-void-black border border-steel/20 shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-steel/20">
          <h2 className="text-2xl font-semibold text-signal-white">Start a Conversation</h2>
          <button
            onClick={onClose}
            className="text-steel hover:text-signal-white transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errorMessage && (
            <div className="p-3 bg-red-900/20 border border-red-700 text-red-400 text-sm">
              {errorMessage}
            </div>
          )}

          <div>
            <label htmlFor="interest-name" className="block text-sm font-medium text-steel mb-1">Name *</label>
            <input
              type="text"
              id="interest-name"
              value={formData.name}
              onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setErrorMessage(''); }}
              required
              className="w-full px-4 py-2 bg-void-black border border-steel/30 text-signal-white placeholder-steel focus:border-command-amber transition-colors"
              placeholder="Your name"
            />
          </div>

          <div>
            <label htmlFor="interest-phone" className="block text-sm font-medium text-steel mb-1">Phone Number *</label>
            <input
              type="tel"
              id="interest-phone"
              value={formData.phone}
              onChange={(e) => { setFormData({ ...formData, phone: e.target.value }); setErrorMessage(''); }}
              required
              className="w-full px-4 py-2 bg-void-black border border-steel/30 text-signal-white placeholder-steel focus:border-command-amber transition-colors"
              placeholder="(314) 555-1234"
            />
          </div>

          <div>
            <label htmlFor="interest-email" className="block text-sm font-medium text-steel mb-1">Email *</label>
            <input
              type="email"
              id="interest-email"
              value={formData.email}
              onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setErrorMessage(''); }}
              required
              className="w-full px-4 py-2 bg-void-black border border-steel/30 text-signal-white placeholder-steel focus:border-command-amber transition-colors"
              placeholder="you@company.com"
            />
          </div>

          <div>
            <label htmlFor="interest-company" className="block text-sm font-medium text-steel mb-1">Company *</label>
            <input
              type="text"
              id="interest-company"
              value={formData.company}
              onChange={(e) => { setFormData({ ...formData, company: e.target.value }); setErrorMessage(''); }}
              required
              className="w-full px-4 py-2 bg-void-black border border-steel/30 text-signal-white placeholder-steel focus:border-command-amber transition-colors"
              placeholder="Your organization"
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary btn-block"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>

          {submitStatus === 'success' && (
            <div className="p-3 bg-green-900/20 border border-green-700 text-green-400 text-sm">
              Thank you. We'll be in touch.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="p-3 bg-red-900/20 border border-red-700 text-red-400 text-sm">
              Something went wrong. Please try again.
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default InterestFormModal;
